import { useEffect, useReducer, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { threadQuery } from "@/api/queries";

/**
 * Follow one agent run over SSE (PRD FR-4.5).
 *
 * `EventSource` is doing more work here than it looks. It authenticates with
 * the session cookie, which is why the streaming endpoint takes no Authorization
 * header; and it resends `Last-Event-ID` on reconnect, which is what makes a
 * dropped connection resume rather than restart. The one thing it does *not*
 * know is when a run is over — so a terminal event closes it explicitly, or it
 * would reconnect forever to a finished run.
 *
 * A run is server-side state, not component state: mounting this against a run
 * that started before the page loaded reattaches to it, and unmounting leaves
 * it running.
 */

export type RunStatus = "idle" | "connecting" | "running" | "complete" | "error" | "stopped";

export interface TraceStep {
  seq: number;
  tool: string;
  sql: string;
  arguments: Record<string, unknown>;
  queryIndex: number | null;
  rowCount: number | null;
  durationMs: number | null;
  truncated: boolean;
  outcome: "pending" | "ok" | "blocked" | "error";
  error: string | null;
  relations: string[];
  /** The source this step ran on, by name — multi-source runs only (MS-3). */
  source: string | null;
  /** For a staged join: the `qN` query indexes it read, and which of them
   *  were cut off at their source's row cap. */
  inputs: number[];
  inputsTruncated: number[];
}

export interface RunState {
  status: RunStatus;
  phase: string;
  modelLabel: string | null;
  steps: TraceStep[];
  /** The answer as it is being composed, decoded out of the JSON payload. */
  text: string;
  warnings: string[];
  error: { code: string; message: string } | null;
  messageId: string | null;
  startedAt: number | null;
  /** Set when this run folded earlier turns into the pinned briefing before
   *  answering (PRD FR-3.4). The divider appears as soon as it happens. */
  compactedThroughSeq: number | null;
}

const IDLE: RunState = {
  status: "idle",
  phase: "",
  modelLabel: null,
  steps: [],
  text: "",
  warnings: [],
  error: null,
  messageId: null,
  startedAt: null,
  compactedThroughSeq: null,
};

type Action =
  | { type: "start" }
  | { type: "status"; data: Record<string, unknown> }
  | { type: "tool_call"; data: Record<string, unknown> }
  | { type: "tool_result"; data: Record<string, unknown> }
  | { type: "warning"; data: Record<string, unknown> }
  | { type: "delta"; data: Record<string, unknown> }
  | { type: "compacted"; data: Record<string, unknown> }
  | { type: "answer"; data: Record<string, unknown> }
  | { type: "error"; data: Record<string, unknown> }
  | { type: "done"; data: Record<string, unknown> }
  | { type: "lost" };

function str(value: unknown): string {
  return typeof value === "string" ? value : "";
}

function num(value: unknown): number | null {
  return typeof value === "number" ? value : null;
}

function numbers(value: unknown): number[] {
  return Array.isArray(value) ? value.filter((item): item is number => typeof item === "number") : [];
}

type StepPatch = Partial<Omit<TraceStep, "seq">>;

function upsertStep(steps: TraceStep[], seq: number, patch: StepPatch): TraceStep[] {
  if (steps.some((step) => step.seq === seq)) {
    return steps.map((step) => (step.seq === seq ? { ...step, ...patch } : step));
  }
  const step: TraceStep = {
    seq,
    tool: "",
    sql: "",
    arguments: {},
    queryIndex: null,
    rowCount: null,
    durationMs: null,
    truncated: false,
    outcome: "pending",
    error: null,
    relations: [],
    source: null,
    inputs: [],
    inputsTruncated: [],
    ...patch,
  };
  return [...steps, step].sort((a, b) => a.seq - b.seq);
}

function reduce(state: RunState, action: Action): RunState {
  switch (action.type) {
    case "start":
      return { ...IDLE, status: "connecting", startedAt: Date.now() };

    case "status": {
      const { data } = action;
      const status = str(data.status);
      // The first frame of a reattached run carries what was missed: the phase,
      // the model, and whatever answer text had already streamed.
      const partial = str(data.partial_text);
      return {
        ...state,
        status: status === "queued" || status === "running" ? "running" : (state.status as RunStatus),
        phase: str(data.phase) || state.phase,
        modelLabel: str(data.model_label) || state.modelLabel,
        messageId: str(data.message_id) || state.messageId,
        text: partial || state.text,
        startedAt: state.startedAt ?? Date.now(),
      };
    }

    case "tool_call": {
      const { data } = action;
      const seq = num(data.seq) ?? state.steps.length + 1;
      return {
        ...state,
        status: "running",
        phase: str(data.phase) || state.phase,
        steps: upsertStep(state.steps, seq, {
          tool: str(data.tool),
          sql: str(data.sql),
          arguments: (data.arguments as Record<string, unknown>) ?? {},
          queryIndex: num(data.query_index),
          outcome: "pending",
          source: str(data.source) || null,
          inputs: numbers(data.inputs),
        }),
      };
    }

    case "tool_result": {
      const { data } = action;
      const seq = num(data.seq) ?? state.steps.length;
      const outcome = str(data.outcome);
      const patch: StepPatch = {
        relations: Array.isArray(data.relations) ? (data.relations as string[]) : [],
        rowCount: num(data.row_count),
        durationMs: num(data.duration_ms),
        truncated: data.truncated === true,
        error: str(data.error) || null,
        outcome:
          outcome === "blocked" ? "blocked" : outcome === "error" || data.error ? "error" : "ok",
      };
      // A blocked statement never got a `tool_call` echo of its SQL, so the
      // result frame is where the trace learns what was refused.
      if (str(data.tool)) patch.tool = str(data.tool);
      if (str(data.sql)) patch.sql = str(data.sql);
      if (str(data.source)) patch.source = str(data.source);
      if (Array.isArray(data.inputs)) patch.inputs = numbers(data.inputs);
      if (Array.isArray(data.inputs_truncated)) patch.inputsTruncated = numbers(data.inputs_truncated);
      return { ...state, steps: upsertStep(state.steps, seq, patch) };
    }

    case "warning": {
      const message = str(action.data.message);
      return message && !state.warnings.includes(message)
        ? { ...state, warnings: [...state.warnings, message] }
        : state;
    }

    case "compacted":
      return { ...state, compactedThroughSeq: num(action.data.through_seq) };

    case "delta":
      return { ...state, status: "running", text: state.text + str(action.data.text) };

    case "answer":
      return {
        ...state,
        text: str(action.data.text) || state.text,
        messageId: str(action.data.message_id) || state.messageId,
      };

    case "error":
      return {
        ...state,
        status: "error",
        error: {
          code: str(action.data.code) || "agent_failed",
          message: str(action.data.message) || "The run failed.",
        },
      };

    case "done": {
      const status = str(action.data.status);
      return {
        ...state,
        status: status === "stopped" ? "stopped" : status === "error" ? "error" : "complete",
        phase: "",
        messageId: str(action.data.message_id) || state.messageId,
      };
    }

    case "lost":
      return {
        ...state,
        status: "error",
        error: {
          code: "stream_lost",
          message: "The connection to this run dropped. Reload to reattach.",
        },
      };
  }
}

const EVENTS = [
  "status",
  "tool_call",
  "tool_result",
  "warning",
  "compacted",
  "delta",
  "answer",
  "error",
  "done",
] as const;

const TERMINAL: RunStatus[] = ["complete", "error", "stopped"];

export interface UseRunStream {
  run: RunState;
  /** True while the agent is working — the composer disables on this. */
  busy: boolean;
  reset: () => void;
}

export function useRunStream(runId: string | null, threadId: string | null): UseRunStream {
  const [run, dispatch] = useReducer(reduce, IDLE);
  const queryClient = useQueryClient();
  const threadRef = useRef(threadId);
  threadRef.current = threadId;

  useEffect(() => {
    if (!runId) return;
    dispatch({ type: "start" });
    const source = new EventSource(`/api/runs/${runId}/events`, { withCredentials: true });

    const listeners = EVENTS.map((name) => {
      const handler = (event: MessageEvent<string>) => {
        let data: Record<string, unknown> = {};
        try {
          data = JSON.parse(event.data) as Record<string, unknown>;
        } catch {
          return;
        }
        dispatch({ type: name, data } as Action);
        if (name === "done" || name === "error") {
          // Nothing more is coming; without this EventSource would reconnect
          // to a finished run on a three-second loop.
          source.close();
          const thread = threadRef.current;
          if (thread) {
            void queryClient.invalidateQueries({ queryKey: threadQuery(thread).queryKey });
            void queryClient.invalidateQueries({ queryKey: ["threads"], exact: true });
          }
        }
      };
      source.addEventListener(name, handler as EventListener);
      return [name, handler] as const;
    });

    const onError = () => {
      // EventSource retries on its own; only a closed socket is fatal.
      if (source.readyState === EventSource.CLOSED) dispatch({ type: "lost" });
    };
    source.addEventListener("error", onError);

    return () => {
      for (const [name, handler] of listeners) {
        source.removeEventListener(name, handler as EventListener);
      }
      source.removeEventListener("error", onError);
      source.close();
    };
  }, [runId, queryClient]);

  return {
    run,
    busy: run.status === "connecting" || run.status === "running",
    reset: () => dispatch({ type: "start" }),
  };
}

/** Elapsed seconds, ticking once a second while a run is in flight. */
export function useElapsed(startedAt: number | null, active: boolean): number {
  const [, tick] = useReducer((count: number) => count + 1, 0);
  useEffect(() => {
    if (!active) return;
    const timer = window.setInterval(tick, 1000);
    return () => window.clearInterval(timer);
  }, [active]);
  if (!startedAt) return 0;
  return Math.max(0, Math.round((Date.now() - startedAt) / 1000));
}

export { TERMINAL as TERMINAL_RUN_STATUSES };
